import React, { FC, MouseEventHandler, useContext } from 'react';
import { BKJButton, BKJFileInput, BKJPillDropdown } from '@awais4123/fillify-design-library';
import { BKJSideModalFooter } from 'components/BKJSideModalFooter';
import { Flex } from 'components/Flex';
import {
  CSVBorderHeaderStyle,
  H3,
  TeamMemberCSVWrapper,
  LocationHeader,
  LocationName,
  DownloadHeader,
  UploadHeader,
  HeaderDescription,
} from 'components/InviteTeamMembersByCSVModal/views/EditRanking/TeamMemberEditRankingView.styled';
import { useInviteTeamMembersByCSVModalDataContext } from 'components/InviteTeamMembersByCSVModal/InviteTeamMembersByCSVModal.data.context';
import {
  TeamMemberEditRankingFormProvider,
  useTeamMemberEditRankingFormContext,
} from 'components/InviteTeamMembersByCSVModal/views/EditRanking/TeamMemberEditRanking.form.context';
import { useTranslation } from 'hooks/useTranslation';
import { TeamMemberPageContext } from 'pages/TeamMemberPage/TeamMemberPage.context';
import {
  CommunitySeniorityMethodContext,
  CommunitySeniorityMethodProvider,
} from 'pages/TeamMemberPage/CommunitySeniorityMethod.context';
import {
  LocationFilterDataProvider,
  useLocationFilterDataContext,
} from 'components/LocationFilterInput/LocationFilterInput.context';

export const EditRankingView: FC = () => {
  const { onChange, values, fieldErrors, onDownload, onSubmit, isDisabled } =
    useTeamMemberEditRankingFormContext();
  const { communities } = useInviteTeamMembersByCSVModalDataContext();
  const {
    values: { location },
  } = useContext(TeamMemberPageContext);
  const result = communities?.find((item) => item.id === Number(location?.id));
  const locationName = result?.name;
  const { t } = useTranslation();
  const {
    data: {
      fetchCommunity: {
        organization: { seniorityMethod },
      },
    },
  } = useContext(CommunitySeniorityMethodContext);

  const { data: options = [] } = useLocationFilterDataContext();

  return (
    <Flex
      flexDirection="column"
      justifyContent="flex-start"
      alignItems="flex-start"
      width="100%"
      flexGrow={1}
      borderTop={CSVBorderHeaderStyle}
    >
      <Flex flexDirection="column" padding="32px 40px 15px" gap="32px">
        <H3>{t('team_member:IMPORT_RANKING')}</H3>
        <TeamMemberCSVWrapper>
          <LocationHeader>{`${t('team_member:LOCATION')} (${t(
            'team_member:CURRENT',
          )})`}</LocationHeader>
          <LocationName>{locationName}</LocationName>
        </TeamMemberCSVWrapper>
        <TeamMemberCSVWrapper>
          <LocationHeader>
            {t('team_member:SENIORITY_CALCULATED_IN')}:{' '}
            {t(`team_member:${seniorityMethod.toUpperCase()}`)}
          </LocationHeader>
        </TeamMemberCSVWrapper>
        <TeamMemberCSVWrapper>
          <DownloadHeader>1. {t('team_member:SELECT_LOCATIONS_FOR_SPREADSHEET')}</DownloadHeader>
          <HeaderDescription>{t('team_member:TEAM_MEMBERS_WILL_BE_RANKED')}</HeaderDescription>
        </TeamMemberCSVWrapper>
        <TeamMemberCSVWrapper>
          <LocationHeader>{t('team_member:SELECT_LOCATIONS_FOR_CSV')}</LocationHeader>
          <BKJPillDropdown
            name="location_ids"
            type="Multi"
            value={values.location_ids}
            onChange={onChange}
            options={options}
            max={20}
            showSelectAll
            validations={[]}
            placeholder={t('team_member:SELECT_LOCATIONS')}
          />
        </TeamMemberCSVWrapper>
        <TeamMemberCSVWrapper>
          <DownloadHeader>2. {t('team_member:DOWNLOAD_SPREADSHEET')}</DownloadHeader>
          <HeaderDescription>
            {t('team_member:EDIT_TEAM_MEMBER_RANKINGS_IN_THE_SPREADSHEET_TEMPLATE')}
          </HeaderDescription>
        </TeamMemberCSVWrapper>
        <BKJButton
          variant="PurpleOutlined"
          width="180px"
          onClick={onDownload}
          type="button"
          disabled={isDisabled}
        >
          {t('team_member:DOWNLOAD_CSV')}
        </BKJButton>
        <TeamMemberCSVWrapper>
          <UploadHeader>3. {t('team_member:UPLOAD_SPREADSHEET')}</UploadHeader>
          <HeaderDescription>
            {t('team_member:UPLOAD_SPREADSHEET_WITH_THE_NEW_RANKINGS')}
          </HeaderDescription>
        </TeamMemberCSVWrapper>
        <div style={{ width: '50%' }}>
          <BKJFileInput
            value={values?.csv}
            name="csv"
            onChange={onChange}
            accept=".csv"
            error={fieldErrors?.csv}
          />
        </div>
      </Flex>

      <Flex height="100%"></Flex>
      <BKJSideModalFooter justifyContent="space-between" flexDirection="row-reverse">
        <BKJButton
          variant="PurpleOutlined"
          width="180px"
          disabled={isDisabled || !values?.csv}
          onClick={onSubmit as unknown as MouseEventHandler<HTMLButtonElement>}
          type="button"
        >
          {t('team_member:SUBMIT')}
        </BKJButton>
      </BKJSideModalFooter>
    </Flex>
  );
};
export const TeamMemberEditRankingView: FC = () => {
  return (
    <CommunitySeniorityMethodProvider>
      <LocationFilterDataProvider>
        <TeamMemberEditRankingFormProvider>
          <EditRankingView />
        </TeamMemberEditRankingFormProvider>
      </LocationFilterDataProvider>
    </CommunitySeniorityMethodProvider>
  );
};
